// /features — capability tour.
// Nine capability blocks covering every surface of the platform. Same
// .section / .section__inner / .cap-card structure used by /docs/* so
// the public surface is visually consistent. No emoji; navy/teal/cream;
// audience-appropriate capability language only — no internal identifiers
// or implementation paths.

import {
    Award,
    BookOpenCheck,
    Crosshair,
    FileCheck2,
    GitBranch,
    Layers,
    Mountain,
    Radar,
    Rocket,
    ShieldCheck,
    Sparkles,
    Users,
    Wind,
} from "lucide-react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const CAPABILITIES = [
    {
        eyebrow: "// AIRFRAME DEPTH",
        title: "Every airframe arrives with its pedigree attached.",
        icon: <Rocket size={22} />,
        body:
            "A seeded library of counter-UAS interceptors and threat airframes, plus a custom builder behind the same validation gates. JSBSim XML round-trips cleanly. Verification flights catch instability at the airframe panel — not in a broken run three hours later.",
        bullets: [
            "Preset library + custom builder with identical validation gates",
            "JSBSim XML import and export with an opinionated round-trip",
            "Verification Flight auto-runs a canned trajectory per airframe",
            "Aero-coefficient tables with CSV paste and live coefficient plots",
        ],
    },
    {
        eyebrow: "// ENVIRONMENT FIDELITY",
        title: "The weather your operators will actually fly in.",
        icon: <Wind size={22} />,
        body:
            "International Standard Atmosphere, non-standard variants, log- and power-law wind profiles, Dryden and Von-Kármán turbulence, gust schedules, and visibility models that flow through to the seeker chain. Every environment ships with a citation and a confidence class — not a guess.",
        bullets: [
            "ISA + non-standard atmospheres, humidity, visibility, solar position",
            "Dryden and Von-Kármán turbulence, scripted gust schedules",
            "Log-law / power-law wind shear with surface-roughness coupling",
            "Three seeded scenarios — Clear Day, Contested Weather, Urban Canyon",
        ],
    },
    {
        eyebrow: "// TERRAIN + RF LINE-OF-SIGHT",
        title: "Terrain that breaks the link — before the live-fire window does.",
        icon: <Mountain size={22} />,
        body:
            "Drop a DEM. Trace obstacles as GeoJSON. The deterministic raycaster propagates the consequences straight to the seeker — per-tick detection events, comms-denied transitions, and a heat-map of denial corridors before a single Monte Carlo sample is drawn.",
        bullets: [
            "OpenTopography DEM picker with inline tile preview",
            "GeoJSON obstacles — polygons, buildings, no-fly boxes",
            "Deterministic raycaster with 4/3-Earth curvature correction",
            "Per-tick link events propagate into seeker + comms-denied triggers",
        ],
    },
    {
        eyebrow: "// THREAT MODELLING",
        title: "Six doctrinal threat modes — each one auditable on its own.",
        icon: <Radar size={22} />,
        body:
            "Single waypoint, patrol, swarm (up to 25 agents), evasive, comms-denied, loitering terminal-commit. Each mode is a distinct schema with its own validation gate — there is no way to silently ship a hybrid that one half of the engine reads differently.",
        bullets: [
            "Single, patrol, swarm, evasive, comms-denied, loitering terminal-commit",
            "Swarm formations up to 25 agents with configurable spacing",
            "Evasive jink schedule editor with live sparkline preview",
            "Comms-denied falls back on time triggers or on-detection events",
        ],
    },
    {
        eyebrow: "// GUIDANCE + SEEKER",
        title: "Pick the guidance law. Defend the choice.",
        icon: <Crosshair size={22} />,
        body:
            "Pure Pursuit, Proportional Navigation, Augmented PN, CLOS, and an adaptive hybrid — each paired with first-class RF or EO-IR seeker parameters. Compare up to four profiles side-by-side on identical Monte Carlo seeds.",
        bullets: [
            "Pure Pursuit, PN, Augmented PN, CLOS, adaptive hybrid",
            "RF and EO-IR seekers with FoV, tracking noise, lock-loss envelopes",
            "Lock-loss → reacquire patterns (last-known cone, widening spiral)",
            "Side-by-side compare of 2-4 profiles on identical seeds",
        ],
    },
    {
        eyebrow: "// ENGAGEMENT + LETHALITY",
        title: "Fuze to p(kill), with the same audit trail throughout.",
        icon: <Award size={22} />,
        body:
            "Warhead, fuze, Pk curves with live monotonicity validation, and full salvo doctrine. Single, salvo, Shoot-Look-Shoot, Shoot-Shoot-Look — every option deterministic per seed, every BDA window governed.",
        bullets: [
            "Warhead, fuze, Pk curve, salvo doctrine — one coherent model",
            "Proximity arming + trigger separation validated before save",
            "Pk-curve editor with live monotonicity check + inline Pk overlay",
            "Salvo doctrines: single, salvo, Shoot-Look-Shoot, Shoot-Shoot-Look",
        ],
    },
    {
        eyebrow: "// CREDIBILITY LAYER",
        title: "Every parameter, sourced. Every result, frozen.",
        icon: <ShieldCheck size={22} />,
        body:
            "Every value carries source, confidence class, and citation at entry. Submitting a run captures an immutable provenance snapshot, grouped by capability — surviving any subsequent library edit. What ran is what ran. Permanently.",
        bullets: [
            "source · confidence · citation on every numerical parameter",
            "Immutable run-start snapshot persists with the run forever",
            "Editing a preset later does not mutate earlier runs' snapshots",
            "Provenance grouped by domain in the run detail + exported reports",
        ],
    },
    {
        eyebrow: "// TWO-LANE EXECUTION",
        title: "Interactive or batch. Same engine. No drift.",
        icon: <Layers size={22} />,
        body:
            "The Workbench runs single scenarios interactively with diagnostic scrubbing. The batch lane sweeps Monte Carlo across thousands of seeds. Same engine drives both — bit-exact on kinematic, metric-exact on JSBSim-hybrid.",
        bullets: [
            "Real-time operator workbench with step-by-step diagnostic scrubber",
            "Batch lane for Monte Carlo + parameter-sweep sensitivity studies",
            "Bit-exact kinematic replays; metric-exact JSBSim-hybrid replays",
            "Same engine drives both lanes — no divergence between them",
        ],
    },
    {
        eyebrow: "// ENTERPRISE WORKFLOW",
        title: "A workbench teams can sign their name to.",
        icon: <Users size={22} />,
        body:
            "Role-based access (Analyst / Reviewer / Admin), per-tenant workspaces, a Cmd+K command palette, and a reviewer state machine covering draft → submitted → approved / rejected / changes-requested. Navy-themed PDF + DOCX reports ship with provenance embedded.",
        bullets: [
            "Role-based workbench — Analyst / Reviewer / Admin",
            "Reviewer state machine with explicit Request-Changes path",
            "Cmd+K command palette for scenario, run, library deep-links",
            "Navy-themed PDF + DOCX reports with embedded provenance",
        ],
    },
    {
        eyebrow: "// DESIGN OPTIMISATION",
        title: "Find the right configuration on a workbench, not a range.",
        icon: <Sparkles size={22} />,
        anchor: "design-optimisation",
        body:
            "UAVSimNet evaluates a proposed interceptor configuration, quantifies its probability of kill, and explores which design changes are most likely to improve outcome probability within defined engineering constraints. The Design Optimisation Workbench ranks parameters by their influence on outcome, generates and evaluates configuration candidates against the engineering envelope, and re-runs the leaders at higher fidelity before promotion.",
        bullets: [
            "Sensitivity ranking — identifies which parameters most affect the outcome and reports their direction of influence",
            "Candidate exploration — generates and evaluates configuration candidates against engineering constraints",
            "Pareto trade-offs — plots probability of kill against design cost and highlights the knee point: the configuration with the strongest improvement per unit of design change",
            "Confirmation runs — re-runs top candidates at higher Monte Carlo fidelity to validate recommendations before promotion",
        ],
        footnote:
            "Includes confidence-interval reporting on every candidate so statistically indistinguishable configurations are flagged together.",
    },
];

const FURTHER_READING = [
    {
        to: "/docs/how-it-works",
        label: "How it works",
        body: "The 5-stage pipeline — assemble, validate, run, analyse, review — told at the capability level.",
        icon: <GitBranch size={18} />,
        testId: "features-cta-docs-how",
    },
    {
        to: "/docs/evidence",
        label: "Evidence framework",
        body: "Validated-vs-provisional registry, citations, bit-exact replays, reviewer chain, immutable snapshots.",
        icon: <FileCheck2 size={18} />,
        testId: "features-cta-docs-evidence",
    },
    {
        to: "/docs/use-cases",
        label: "Counter-UAS use cases",
        body: "Three narratives — urban-canyon net capture, contested-weather salvo, group-1 swarm vs kinetic.",
        icon: <BookOpenCheck size={18} />,
        testId: "features-cta-docs-use-cases",
    },
];

export default function FeaturesPage() {
    return (
        <>
            <SeoHead
                path="/features"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/features", label: "Features" }),
                ]}
            />
            <div data-testid="features-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// CAPABILITY TOUR</span>
                        <h1 className="section__heading">
                            Ten capabilities. One engine. Every result, defensible.
                        </h1>
                        <p className="section__lede">
                            Counter-UAS programmes fail when the platform that models
                            them is stitched together from disconnected tools. UAVSimNet
                            is one engine, one data model, and one reviewer-signed
                            evidence trail — covering every surface from an airframe's
                            aero coefficients to an engagement's probability of kill.
                        </p>
                    </div>
                </section>

                {CAPABILITIES.map((c, idx) => (
                    <section
                        key={c.title}
                        id={c.anchor || undefined}
                        className={"section" + (idx % 2 === 1 ? " section--alt" : "")}
                        data-testid={
                            c.anchor
                                ? `features-section-${c.anchor}`
                                : `features-section-${idx}`
                        }
                    >
                        <div
                            className="section__inner"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 48,
                                alignItems: "start",
                            }}
                        >
                            <div className="scroll-reveal">
                                <div className="cap-card__icon">{c.icon}</div>
                                <span className="section__eyebrow">{c.eyebrow}</span>
                                <h2
                                    className="section__heading"
                                    style={{ fontSize: 26, lineHeight: 1.25 }}
                                >
                                    {c.title}
                                </h2>
                            </div>
                            <div className="scroll-reveal" data-delay="120">
                                <p className="section__lede" style={{ marginBottom: 18 }}>
                                    {c.body}
                                </p>
                                <ul className="cap-card__list">
                                    {c.bullets.map((b) => (
                                        <li key={b}>{b}</li>
                                    ))}
                                </ul>
                                {c.footnote && (
                                    <p
                                        style={{
                                            marginTop: 14,
                                            color: "var(--ux-text-3)",
                                            fontSize: 12,
                                            fontStyle: "italic",
                                        }}
                                        data-testid={`features-section-${c.anchor || idx}-footnote`}
                                    >
                                        {c.footnote}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                ))}

                <section className="section">
                    <div className="section__inner">
                        <span className="section__eyebrow">// FURTHER READING</span>
                        <h2 className="section__heading" style={{ fontSize: 28 }}>
                            Read on — how it works, why the evidence holds up, and what
                            the use cases look like.
                        </h2>
                        <div
                            style={{
                                marginTop: 32,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: 20,
                            }}
                            data-testid="features-further-reading"
                        >
                            {FURTHER_READING.map((c) => (
                                <Link
                                    key={c.to}
                                    to={c.to}
                                    className="cap-card"
                                    style={{ display: "block", color: "inherit", textDecoration: "none" }}
                                    data-testid={c.testId}
                                >
                                    <div className="cap-card__icon">{c.icon}</div>
                                    <div className="cap-card__title">{c.label}</div>
                                    <p className="cap-card__body">{c.body}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="section">
                    <div className="section__inner" style={{ textAlign: "center" }}>
                        <h2 className="section__heading">See it on your own data.</h2>
                        <p
                            className="section__lede"
                            style={{ margin: "0 auto 24px", maxWidth: 720 }}
                        >
                            We brief evaluation teams on the platform, your threat
                            profile, and the deployment shape that fits.
                        </p>
                        <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                            <Link
                                to="/contact"
                                className="btn-primary"
                                data-testid="features-cta-contact"
                            >
                                Request an Assessment
                            </Link>
                            <Link
                                to="/demo"
                                className="btn-ghost"
                                data-testid="features-cta-demo"
                            >
                                See verified intercept demonstrations
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
